import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/datatable.scss";

/**
 * ACH Files — the admin side of the orchestrated RBC ACH pipeline (B3).
 *
 * 1. Eligible investments: every ACH-routed UNPAID order with per-row blocker
 *    flags (PAD authorization, bank coordinates, signed documents,
 *    suitability). Only fully eligible rows can be pulled into a file.
 * 2. Generate: the API rebuilds the payment list server-side (full account
 *    numbers never reach this browser) and calls the ACH service; replays of
 *    the same batch return the stored file instead of minting a new one.
 * 3. Files: lifecycle GENERATED → UPLOADED_TO_RBC → SETTLED / RETURNED (or
 *    VOID before upload). Settling marks the included investments PAID;
 *    returns revert the returned subset to UNPAID for a retry file.
 */
const API = process.env.REACT_APP_API;

const BLOCKER_LABELS = {
  padAuthorized: "PAD",
  // ACH debits need a Canadian institution/transit/account; an investor who
  // saved an international (IBAN) account is not a debit-file candidate.
  bankDetailsComplete: "Bank details (CA account)",
  // ACH094 files are ASCII-only; a name with no representable characters
  // (A-Z, 0-9) after normalization cannot go on a debit record.
  achNameSafe: "Name (ACH charset)",
  signingSatisfied: "Documents",
  suitabilityAccepted: "Suitability",
  validAmount: "Amount",
};

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

const AchFiles = () => {
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [inv, fl] = await Promise.all([
        axios.post(`${API}admin/ach/eligible-investments`, {}, { headers: authHeaders() }),
        axios.get(`${API}admin/ach/files`, { headers: authHeaders() }),
      ]);
      setEligible(inv?.data?.investments || []);
      setFiles(fl?.data?.files || []);
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load ACH data",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    if (!selected.length) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}admin/ach/generate-file`,
        { investmentIds: selected, dueDate },
        { headers: authHeaders() }
      );
      const file = res?.data?.file;
      Swal.fire({
        title: res?.data?.replayed ? "File already generated (replay)" : "ACH file generated",
        text: file ? `${file.fileName} — ${file.entryCount} entries, $${file.totalAmount}` : "",
        icon: "success",
        confirmButtonText: "OK",
      });
      setSelected([]);
      await fetchAll();
    } catch (error) {
      const blockers = error?.response?.data?.blockers;
      Swal.fire({
        title: "ACH generation failed",
        text:
          (error?.response?.data?.message || error.message) +
          (blockers ? ` Blocked investments: ${Object.keys(blockers).join(", ")}` : ""),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}admin/ach/files/${file.id}/download`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({ title: "Download failed", icon: "error", confirmButtonText: "OK" });
    }
  };

  const transition = async (file, status) => {
    let body = { status };
    if (status === "RETURNED") {
      const { value } = await Swal.fire({
        title: "Returned entries",
        input: "text",
        inputLabel: "Comma-separated investment IDs that were returned by RBC",
        showCancelButton: true,
      });
      if (!value) return;
      body.returns = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((id) => ({ investmentId: Number(id), reason: "RBC return" }));
    } else {
      const confirm = await Swal.fire({
        title: `Mark file as ${status.replace(/_/g, " ")}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
      });
      if (!confirm.isConfirmed) return;
    }
    setLoading(true);
    try {
      await axios.patch(`${API}admin/ach/files/${file.id}/status`, body, {
        headers: authHeaders(),
      });
      await fetchAll();
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "Status update failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const eligibleColumns = [
    { field: "investmentId", headerName: "ID", width: 80 },
    { field: "investor", headerName: "Investor", flex: 1, minWidth: 160 },
    { field: "campaign", headerName: "Campaign", flex: 1, minWidth: 180 },
    { field: "amount", headerName: "Amount", width: 110 },
    { field: "referenceCode", headerName: "Reference", width: 150 },
    {
      field: "eligible",
      headerName: "Blockers",
      flex: 1,
      minWidth: 220,
      renderCell: (params) =>
        params.row.eligible ? (
          <Chip label="Ready" color="success" size="small" />
        ) : (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {Object.entries(params.row.blockers || {})
              .filter(([, ok]) => !ok)
              .map(([k]) => (
                <Chip key={k} label={BLOCKER_LABELS[k] || k} color="warning" size="small" />
              ))}
          </div>
        ),
    },
  ];

  const fileColumns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "file_name", headerName: "File", flex: 1, minWidth: 220 },
    { field: "purpose", headerName: "Purpose", width: 190 },
    { field: "direction", headerName: "Dir", width: 80 },
    { field: "entry_count", headerName: "Entries", width: 90 },
    { field: "total_amount", headerName: "Total", width: 110 },
    { field: "due_date", headerName: "Due", width: 110 },
    { field: "status", headerName: "Status", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 320,
      renderCell: (params) => {
        const f = params.row;
        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => downloadFile(f)}>
              Download
            </Button>
            {f.status === "GENERATED" && (
              <>
                <Button size="small" variant="contained" onClick={() => transition(f, "UPLOADED_TO_RBC")}>
                  Mark uploaded
                </Button>
                <Button size="small" color="error" onClick={() => transition(f, "VOID")}>
                  Void
                </Button>
              </>
            )}
            {f.status === "UPLOADED_TO_RBC" && (
              <>
                <Button size="small" color="success" variant="contained" onClick={() => transition(f, "SETTLED")}>
                  Settled
                </Button>
                <Button size="small" color="warning" onClick={() => transition(f, "RETURNED")}>
                  Returned
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <PageShell>
      <PageHead title="ACH Files" />
      <div className="datatable">
        <Loader isLoading={loading} />
        <div className="datatableTitle">
          <h5>ACH — eligible investments (pre-authorized debits)</h5>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <label htmlFor="ach-due-date" style={{ fontSize: 14 }}>
              Due date
              <input
                id="ach-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ marginLeft: 8, padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
              />
            </label>
            <Button
              variant="contained"
              color="primary"
              disabled={!selected.length}
              onClick={handleGenerate}
            >
              Generate debit file ({selected.length})
            </Button>
          </div>
        </div>
        <DataGrid
          className="datagrid"
          autoHeight
          rows={eligible}
          getRowId={(r) => r.investmentId}
          columns={eligibleColumns}
          checkboxSelection
          isRowSelectable={(params) => params.row.eligible}
          selectionModel={selected}
          onSelectionModelChange={(sel) =>
            setSelected(
              sel.filter((id) => eligible.find((e) => e.investmentId === id && e.eligible))
            )
          }
          hideFooterPagination
        />

        <div className="datatableTitle" style={{ marginTop: 32 }}>
          <h5>Generated files</h5>
        </div>
        <DataGrid
          className="datagrid"
          autoHeight
          rows={files}
          columns={fileColumns}
          hideFooterPagination
        />
      </div>
    </PageShell>
  );
};

export default AchFiles;
